import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import moment from 'moment';
import * as qs from 'query-string';
import React from 'react';
import { connect } from 'react-redux';

import { Membership } from '@aps-management/primapp-common';

import { Screen } from '_components/core';
import apolloClient from '_utils/apolloClient';
import i18n from '_utils/i18n';
// `checkMembershipSchedule` n'existe que sur master : elle n'est pas déclarée dans le
// schéma de primapp-api, d'où un second client sur cet écran.
import masterClient from '_utils/masterClient';

import { checkMembershipSchedule, isScheduleForMembership } from '../../services/membership';

/* */
const styles = (theme) => ({
  summary: {
    marginBottom: `${theme.spacing.unit * 6}px`,
  },
  summaryCardActions: {
    display: 'inherit',
    padding: theme.spacing.unit * 1,
  },
  card: {
    margin: `${theme.spacing.unit * 3}px 0`,
  },
  cardHeader: {
    background: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  cardContent: {
    padding: 0,
  },
  cardActions: {
    justifyContent: 'flex-end',
  },
  remaining: {
    color: theme.palette.error.main,
  },
  payButton: {
    margin: `0 0 ${theme.spacing.unit * 1}px`,
  },
  bold: { fontWeight: 'bold' },
  amount: { marginLeft: '.5rem', marginRight: '.5rem' },
});

/* */
class MembershipList extends React.Component {
  /* */
  constructor(props) {
    super(props);

    this.state = {
      // Screen
      error: null,
      loading: false,
      // Data
      exercises: [],
      // Échéanciers DÉJÀ enregistrés chez nous, par référence de cotisation. Sert à dire au
      // golfeur ce qui existe avant de lui proposer d'en créer un.
      plansExistants: {},
    };

    const { redirect = null } = qs.parse(props.location.search);
    this.redirectTo = redirect;

    this.due = {
      total: 0,
      memberships: [],
    };
    this.options = [];

    this.globalPayment = {};

    this.subscriptionAllowed = false;
  }

  /* */
  componentDidMount() {
    this.load();
  }

  /* */
  load = () => {
    const { golf } = this.props;

    this.setState({
      error: null,
      exercises: [],
      loading: true,
    });

    Membership.api
      .getExercises(apolloClient, { golfId: golf.id })
      .then((res) => {
        if (!res.exercises) throw new Error('errors.user.not_found');

        const { exercises, globalPayment, subscriptionAllowed } = res.exercises;

        const options = globalPayment.caseOptions
          ? globalPayment.caseOptions.map((o) => o.option)
          : globalPayment.options;

        this.globalPayment = globalPayment;
        this.subscriptionAllowed = subscriptionAllowed;

        // init options
        this.options = (options || []).reduce(
          (opts, option) => ({
            ...opts,
            [option]: 0,
          }),
          {},
        );

        this.checkDue(exercises);
        this.setState({ exercises });
        return this.chargerPlansExistants();
      })
      .catch((e) => this.setState({ error: e.message }))
      .finally(() => this.setState({ loading: false }));
  };

  /**
   * Relit, pour chaque cotisation due, l'échéancier RÉELLEMENT enregistré.
   *
   * La liste se construit sur `getExercises`, qui vient du PGB du golf : son
   * `paymentSchedule` peut être vide alors qu'un plan existe chez nous. On proposait donc
   * « Payer en N mensualités » pour un plan déjà en place, et le golfeur ne l'apprenait
   * qu'au dernier clic. Même relecture que les écrans publics `ConfirmMembership` et
   * `PayMembership`, via `masterClient` : la requête n'existe que sur master.
   *
   * Silencieuse en cas d'échec : on n'empêche jamais l'affichage ni la souscription pour
   * une information qu'on n'a pas pu obtenir.
   */
  chargerPlansExistants = () => {
    const { golf, account } = this.props;
    const { memberships } = this.due;

    if (!account || !account.id || !memberships.length) return Promise.resolve();

    return Promise.all(
      memberships.map((m) =>
        checkMembershipSchedule(masterClient, {
          golfId: golf.id,
          accountId: account.id,
          reference: m.reference,
        }).then((stocke) => (isScheduleForMembership(stocke, m) ? { reference: m.reference, stocke } : null)),
      ),
    )
      .then((lignes) => {
        const plansExistants = lignes.filter(Boolean).reduce((acc, l) => ({ ...acc, [l.reference]: l.stocke }), {});
        this.setState({ plansExistants });
      })
      .catch(() => {});
  };

  /* */
  checkDue(exercises) {
    const memberships = [];
    exercises.forEach((e) => {
      e.memberships.forEach((m) => {
        if (m.total.due > 0) {
          memberships.push(m);
        }
        if (m.total.options) {
          m.total.options.forEach(({ option, amount }) => {
            this.options[option] = (this.options[option] || 0) + amount;
          });
        }
      });
    });

    const total = memberships.reduce((counter, membership) => counter + membership.total.due, 0);

    this.due = {
      total,
      memberships,
      redirect: this.redirectTo,
    };
  }

  /* */
  goToDetails(membership) {
    this.props.setMembership(membership);
    this.props.history.push('/membership/view');
  }

  /* */
  goToCashPayment() {
    const { due } = this;
    due.memberships.forEach((m, i) => {
      due.memberships[i] = {
        ...m,
        // compute membership total due based on option
        amount: m.total.due,
        transaction: {
          cash: true,
          schedule: null,
        },
      };
    });
    this.props.updateDue({
      ...due,
      cash: true,
      scheduleStartDate: null,
    });
    this.props.history.push('/membership/payment');
  }

  /* */
  goToApproveSchedule(months) {
    const { due, globalPayment } = this;

    due.memberships.forEach((m, i) => {
      // compute membership total due based on option
      const option = (m.total.options || []).find((o) => o.option === Number(months));
      due.memberships[i] = {
        ...m,
        amount: option ? option.amount : m.total.due,
        transaction: {
          cash: false,
          schedule: [],
        },
      };
    });

    const caseOption = (globalPayment.caseOptions || []).find((o) => o.option === Number(months));
    this.props.updateDue({
      ...due,
      cash: false,
      options: this.options,
      total: this.options[months] || due.total,
      scheduleStartDate: caseOption ? caseOption.scheduleStartDate : globalPayment.scheduleStartDate,
    });
    this.props.history.push(`/membership/schedule/${months}`);
  }

  /* */
  renderMembership(membership, index) {
    const { classes, wording } = this.props;

    const type = Membership.functions.isPrincipal(membership) ? 'principal' : 'additional';
    const scopeTitle = `membership.titles.${type}_membership_${wording.membership}`;
    const createdAt = moment(membership.createdAt, 'YYYYMMDD').toDate();
    const totalAmount = membership.total.amount / 100;
    const remainingAmount = (membership.total.amount - membership.total.paid) / 100;

    return (
      <Card className={classes.card} key={index}>
        <CardHeader
          title={i18n.t(scopeTitle)}
          className={classes.cardHeader}
          titleTypographyProps={{ color: 'inherit', variant: 'body1' }}
        />
        <CardContent className={classes.cardContent}>
          <List disablePadding>
            <ListItem divider>
              <ListItemText primary={i18n.l('date.formats.default', createdAt)} />
              <Typography variant='subtitle1'>{i18n.l('currency', totalAmount)}</Typography>
            </ListItem>
            <ListItem>
              <ListItemText primary={'Montant restant dû'} />
              <Typography className={classNames({ [classes.remaining]: remainingAmount > 0 })} variant='subtitle1'>
                {i18n.l('currency', remainingAmount)}
              </Typography>
            </ListItem>
          </List>
        </CardContent>
        <CardActions className={classes.cardActions}>
          <Button color='secondary' variant='contained' onClick={() => this.goToDetails(membership)}>
            {'Plus de détails'}
          </Button>
        </CardActions>
      </Card>
    );
  }

  /* */
  renderExercisesList() {
    const { exercises } = this.state;
    const { subscriptionAllowed } = this;

    const nothing = exercises.length === 0 || (exercises.length === 1 && exercises[0].memberships.length === 0);

    return !nothing
      ? exercises.map((e) => this.renderExercise(e))
      : !subscriptionAllowed && (
          <Typography align='center' variant='h5'>
            {'Aucune cotisation.'}
          </Typography>
        );
  }

  /* */
  renderExercise(exercise) {
    return (
      <React.Fragment key={exercise.year}>
        <Typography variant='h5' gutterBottom>
          {exercise.year}
        </Typography>
        <Divider />
        {exercise.memberships.map((membership, i) => this.renderMembership(membership, i))}
      </React.Fragment>
    );
  }

  /* */
  renderPaymentOption(months, i) {
    const { due, globalPayment } = this;
    const { classes } = this.props;

    const caseOption = (globalPayment.caseOptions || []).find((o) => o.option === Number(months));
    const scheduleDeadlineDate = moment.max(
      moment(),
      caseOption ? moment(caseOption.scheduleDeadlineDate) : moment(globalPayment.scheduleDeadlineDate),
    );
    const scheduleStartDate = moment.max(
      moment().add(1, 'days'),
      caseOption ? moment(caseOption.scheduleStartDate) : moment(globalPayment.scheduleStartDate),
    );

    const text =
      this.options[months] && this.options[months] !== due.total ? (
        <span className={classes.amount}>{i18n.l('currency', this.options[months] / 100)}</span>
      ) : (
        ''
      );

    return (
      <React.Fragment key={i}>
        <Button
          fullWidth
          size='medium'
          color='secondary'
          variant='outlined'
          className={classes.payButton}
          onClick={() => this.goToApproveSchedule(months)}
        >
          {'Payer'}
          {text}
          {` en ${months} mensualités`}
        </Button>
        {(scheduleDeadlineDate || scheduleStartDate) && (
          <Box mb={2}>
            {scheduleDeadlineDate.isValid() && (
              <Typography color='textSecondary' align='center'>
                {`Vous avez la possibilité de passer en paiement mensuel jusqu'au ${i18n.l('date.formats.default', scheduleDeadlineDate.toDate())}. `}
              </Typography>
            )}
            {scheduleStartDate.isValid() && (
              <Typography color='textSecondary' align='center'>
                {`La première échéance sera au ${i18n.l('date.formats.default', scheduleStartDate.toDate())}.`}
              </Typography>
            )}
          </Box>
        )}
      </React.Fragment>
    );
  }

  /**
   * Ce qui est DÉJÀ en place, dit avant toute proposition.
   *
   * On n'escamote rien : le plan enregistré est décrit en clair, avec son montant, son
   * nombre d'échéances et sa première date. C'est une information due au golfeur, et la
   * seule façon qu'il comprenne pourquoi on ne lui propose plus de mensualiser.
   */
  renderPlanExistant(membership) {
    const { plansExistants } = this.state;
    const { classes } = this.props;
    const stocke = plansExistants[membership.reference];

    if (!stocke) return null;

    const echeances = (stocke.paymentSchedule || []).filter(Boolean);
    const total = typeof stocke.amount === 'number'
      ? stocke.amount
      : echeances.reduce((t, e) => t + (e.amount || 0), 0);
    const premiere = echeances.length ? echeances[0].date : null;

    /*
     * Statut d'échéance calculé par master à partir des transactions réelles :
     * 0 = à venir, 1 = réglée, 2 = en échec (`PAYMENT_STATUS_SUCCESS` / `_FAILURE`).
     *
     * On le dit, parce que c'est souvent l'explication de tout : sur le compte qui a servi
     * à éprouver cet écran, deux prélèvements avaient échoué début juillet, et c'est
     * précisément pour cela que le plan a été suspendu ensuite.
     */
    const reglees = echeances.filter((e) => e.status === 1).length;
    const echecs = echeances.filter((e) => e.status === 2).length;
    const etat = [];
    if (reglees > 0) etat.push(`${reglees} échéance${reglees > 1 ? 's' : ''} déjà réglée${reglees > 1 ? 's' : ''}`);
    if (echecs > 0) etat.push(`${echecs} échéance${echecs > 1 ? 's' : ''} en échec`);

    return (
      <Box mt={1} mb={1} key={`plan-${membership.reference}`}>
        <Typography variant='subtitle1' className={classes.bold}>
          {`Vous avez déjà un plan de prélèvement pour la cotisation ${membership.year}`}
        </Typography>
        <Typography color='textSecondary'>
          {`${i18n.l('currency', total / 100)}`}
          {echeances.length > 0 ? ` en ${echeances.length} mensualités` : ''}
          {premiere ? `, à partir du ${i18n.l('date.formats.default', moment(premiere, 'YYYYMMDD').toDate())}.` : '.'}
        </Typography>
        {etat.length > 0 && (
          <Typography color={echecs > 0 ? 'error' : 'textSecondary'}>
            {`${etat.join(', ')}${echecs > 0 ? ' - contactez votre golf.' : '.'}`}
          </Typography>
        )}
        {/* `workflowState` n'est rendu par master que si son résolveur le sélectionne : tant
            qu'il vaut null, on n'affirme rien sur l'état du plan plutôt que de rassurer à tort. */}
        {stocke.workflowState === 'locked' && (
          <Typography color='error'>
            {'Ce plan est actuellement suspendu : il ne donnera lieu à aucun prélèvement. Contactez votre golf.'}
          </Typography>
        )}
      </Box>
    );
  }

  /* */
  renderDue() {
    const { due } = this;
    const { plansExistants } = this.state;
    const { classes, wording } = this.props;

    if (due.total === 0) return null;

    /*
     * On ne retire la mensualisation que si elle n'a PLUS D'OBJET, c'est-à-dire si toutes
     * les cotisations dues portent déjà un plan. S'il en reste une sans plan, le bouton
     * garde son sens pour elle et on le laisse : le bloc au-dessus explique la situation.
     * Le paiement comptant reste proposé dans tous les cas - régler comptant malgré un
     * échéancier existant est légitime.
     */
    const toutesDejaPlanifiees =
      due.memberships.length > 0 && due.memberships.every((m) => Boolean(plansExistants[m.reference]));

    const restOptions = toutesDejaPlanifiees
      ? []
      : Object.keys(this.options || {}).filter((opt) => opt > 1);

    return (
      <div className={classes.summary}>
        <Typography variant='h5' gutterBottom>
          {'A régler'}
        </Typography>
        <Divider />
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <List disablePadding>
              {due.memberships.map((membership, i) => {
                const type = Membership.functions.isPrincipal(membership) ? 'principal' : 'additional';
                const scopeTitle = `membership.titles.${type}_membership_${wording.membership}`;

                return (
                  <ListItem divider key={i}>
                    <ListItemText primary={`${membership.year} - ${i18n.t(scopeTitle)}`} />
                    <Typography variant='subtitle1'>{i18n.l('currency', membership.total.due / 100)}</Typography>
                  </ListItem>
                );
              })}
              <ListItem divider>
                <ListItemText primary='Total' classes={{ primary: classes.bold }} />
                <Typography variant='subtitle1' className={classes.bold}>
                  {i18n.l('currency', due.total / 100)}
                </Typography>
              </ListItem>
            </List>
            <Box pl={2} pr={2}>
              {due.memberships.map((m) => this.renderPlanExistant(m))}
            </Box>
          </CardContent>
          <CardActions className={classes.summaryCardActions}>
            {Boolean(this.options[1]) && (
              <Button
                fullWidth
                size='medium'
                color='secondary'
                variant='contained'
                className={classes.payButton}
                onClick={() => this.goToCashPayment()}
              >
                {'Payer comptant'}
              </Button>
            )}
            {restOptions.length > 0 && restOptions.map(this.renderPaymentOption.bind(this))}
          </CardActions>
        </Card>
      </div>
    );
  }

  /* */
  renderSubscribeButton() {
    const { subscriptionAllowed } = this;

    if (!subscriptionAllowed) return null;

    return (
      <Box display='flex' flexDirection='row' alignItems='center'>
        <Typography variant='h5' style={{ marginRight: 20 }}>
          {'Vous souhaitez devenir membre ?'}
        </Typography>
        <Button
          size='medium'
          color='secondary'
          variant='contained'
          onClick={() => this.props.history.push('/membership/subscribe')}
        >
          {"S'abonner"}
        </Button>
      </Box>
    );
  }

  /* */
  render() {
    const { wording } = this.props;
    const { error, loading } = this.state;
    const title = i18n.t(`home.titles.my_membership_${wording.membership}`);

    return (
      <Screen error={error} title={title} loading={loading} onRetry={this.load}>
        {this.renderSubscribeButton()}
        {this.renderDue()}
        {this.renderExercisesList()}
      </Screen>
    );
  }
}

const mapStateToProps = ({ app, membershipData }) => ({
  golf: app.golf,
  // `checkMembershipSchedule` exige l'identifiant du compte : (golf, référence) n'est pas
  // unique et peut porter plusieurs golfeurs.
  account: app.account,
  data: membershipData,
  wording: app.golf.options.wording,
});

const StyledComponent = withStyles(styles)(MembershipList);

export default connect(mapStateToProps, Membership.actions)(StyledComponent);
